"use client";

/**
 * 首页推荐区块（版式与找政策 / 找活动列表页不同，单独成组件）：
 * - 政策：一张大卡 + 右侧列表（news-grid）
 * - 活动：未结束活动最多 3 条（home-event-grid）
 * - 投资：一排两张、可左右滑动的横向轮播（invest-home）
 * OPC / 产品 / 园区首页推荐直接复用 OpcCard / ProductCard / ParkDirectory(capped)。
 */
import { useRef } from "react";
import { OpcCard, ProductCard, InvestCard } from "./cards";
import type { OpcItem, OpcProduct } from "@/common/interface/opc";
import type { PolicyItem } from "@/common/interface/policy";
import type { ActivityItem } from "@/common/interface/activity";
import type { InvestOpportunity } from "@/common/interface/invest";
import { sortByDateDesc, isLatest } from "@/common/policies/render";
import { sortInvest } from "@/common/invest/render";
import {
  upcomingActivities,
  monthDay,
  statusKey,
  activityStatus,
  HOME_ACTIVITY_LIMIT,
} from "@/common/activities/render";

const openExternal = (url?: string) => {
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

/** 首页政策推荐：最新 1 条大卡 + 其后 listCount 条列表 */
export function PolicyHome({ policies, listCount = 4 }: { policies: PolicyItem[]; listCount?: number }) {
  const picks = sortByDateDesc(policies).slice(0, listCount + 1);
  if (!picks.length) return <p className="park-empty">政策信息完善中</p>;
  const [top, ...rest] = picks;
  return (
    <div className="news-grid">
      <article className="news-feature" data-url={top.url} onClick={() => openExternal(top.url)}>
        {isLatest(top.date) && <b className="policy-new">最新</b>}
        <span>{top.city} · {top.level}</span>
        <h2>{top.title}</h2>
        <p>{top.summary}</p>
        <button>查看政策原文 →</button>
      </article>
      <div className="news-list">
        {rest.map((p) => (
          <button key={p.url} data-url={p.url} onClick={() => openExternal(p.url)}>
            <span>{p.level}</span>
            <b>{p.title}</b>
            <small>{p.city} · {p.date}{isLatest(p.date) ? " · 最新" : ""}</small>
            <em>→</em>
          </button>
        ))}
      </div>
    </div>
  );
}

/** 首页活动推荐：只取未结束活动，最多 listCount 条 */
export function ActivityHome({
  activities,
  listCount = HOME_ACTIVITY_LIMIT,
}: {
  activities: ActivityItem[];
  listCount?: number;
}) {
  const list = upcomingActivities(activities).slice(0, listCount);
  if (!list.length) {
    return <p className="park-empty">近期暂无进行中的活动，可查看往期活动回顾</p>;
  }
  return (
    <div className="home-event-grid">
      {list.map((a) => {
        const st = activityStatus(a);
        return (
          <article key={a.url}>
            <div>
              <span>{a.city}</span>
              <b>{monthDay(a.startDate)}</b>
              <i className={`activity-status activity-status--${statusKey(st)}`}>{st}</i>
            </div>
            <small>{a.category}</small>
            <h2>{a.title}</h2>
            <p>{a.summary}</p>
            <em>{a.venue}</em>
            <a href={a.url} target="_blank" rel="noreferrer">查看官方公告 ↗</a>
          </article>
        );
      })}
    </div>
  );
}

/** 首页 OPC 推荐：opc-grid.home-opc-grid，取前 limit 家 */
export function OpcHome({
  opcs,
  products,
  limit = 3,
}: {
  opcs: OpcItem[];
  products: OpcProduct[];
  limit?: number;
}) {
  if (!opcs.length) return null;
  return (
    <section className="opc-grid home-opc-grid">
      {opcs.slice(0, limit).map((o) => (
        <OpcCard key={o.id} opc={o} products={products} />
      ))}
    </section>
  );
}

/** 首页产品推荐：opc-grid.product-grid.home-product-grid，取前 limit 个 */
export function ProductHome({ products, limit = 4 }: { products: OpcProduct[]; limit?: number }) {
  if (!products.length) return null;
  return (
    <section className="opc-grid product-grid home-product-grid">
      {products.slice(0, limit).map((p) => (
        <ProductCard key={p.id} product={p} />
      ))}
    </section>
  );
}

/**
 * 首页找投资推荐：一排两张、可左右滑动的横向轮播。
 * 卡片版式复用找投资列表页的 .public-opportunity-list + InvestCard，
 * 外层 .invest-home-track 做 scroll-snap，两侧按钮控制滚动。
 */
export function InvestHome({ list, limit = 8 }: { list: InvestOpportunity[]; limit?: number }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const items = sortInvest(list).slice(0, limit);
  if (!items.length) {
    return <p className="park-empty">投资活动信息完善中</p>;
  }
  const scrollBy = (dir: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    // 每次滚动约一屏（两张卡片）宽度
    track.scrollBy({ left: dir * track.clientWidth, behavior: "smooth" });
  };
  return (
    <div className="invest-home">
      <button
        type="button"
        className="invest-home-nav invest-home-nav--prev"
        aria-label="上一组"
        onClick={() => scrollBy(-1)}
      >
        ‹
      </button>
      <div className="invest-home-track public-opportunity-list" ref={trackRef}>
        {items.map((i) => (
          <InvestCard key={i.slug} item={i} home />
        ))}
      </div>
      <button
        type="button"
        className="invest-home-nav invest-home-nav--next"
        aria-label="下一组"
        onClick={() => scrollBy(1)}
      >
        ›
      </button>
    </div>
  );
}
